import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Heading,
  HStack,
  Input,
  IconButton,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import FilterBadge from '../../components/FilterBadge';

const AdminChatSoporte = () => {
  const toast = useToast();
  const { accessToken: token } = useAuth();
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState('');
  const [channels, setChannels] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [telegramReason, setTelegramReason] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [flowFilter, setFlowFilter] = useState('all');
  const [assistantFilter, setAssistantFilter] = useState('all');
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [eustaLogs, setEustaLogs] = useState('');
  const [chichoLogs, setChichoLogs] = useState('');
  const [autoRepliesByChannel, setAutoRepliesByChannel] = useState({});
  const [lastViewedByChannel, setLastViewedByChannel] = useState({});
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [manualRefreshNonce, setManualRefreshNonce] = useState(0);
  const [adminMessagesPollIntervalSeconds, setAdminMessagesPollIntervalSeconds] = useState(12);
  const [adminChannelsPollIntervalSeconds, setAdminChannelsPollIntervalSeconds] = useState(30);
  const [adminLogsPollIntervalSeconds, setAdminLogsPollIntervalSeconds] = useState(60);
  const [adminTelegramStatusPollIntervalSeconds, setAdminTelegramStatusPollIntervalSeconds] = useState(60);
  const [isPageVisible, setIsPageVisible] = useState(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible'
  );
  const DUPLICATE_WINDOW_MS = 10000;

  const isSameMessage = useCallback((a, b) => {
    if (!a || !b) return false;
    if ((a.sender_type || a.senderType) !== (b.sender_type || b.senderType)) return false;
    if ((a.mensaje || a.text) !== (b.mensaje || b.text)) return false;
    if (!a.fecha_envio || !b.fecha_envio) return false;
    const diff = Math.abs(new Date(a.fecha_envio).getTime() - new Date(b.fecha_envio).getTime());
    return diff <= DUPLICATE_WINDOW_MS;
  }, [DUPLICATE_WINDOW_MS]);

  const mergeMessages = useCallback((serverMessages, localMessages) => {
    const merged = [...serverMessages];
    const locals = (localMessages || []).filter((msg) => msg.local_only);
    locals.forEach((localMsg) => {
      const exists = merged.some((srv) => isSameMessage(srv, localMsg));
      if (!exists) {
        merged.push(localMsg);
      }
    });
    return merged;
  }, [isSameMessage]);

  const filterDuplicates = (items) => {
    const result = [];
    items.forEach((msg) => {
      const exists = result.some((prev) => isSameMessage(prev, msg));
      if (!exists) {
        result.push(msg);
      }
    });
    return result;
  };

  const apiBaseUrl = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_BASE_URL;
    if (envUrl) {
      if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && envUrl.startsWith('http://')) {
        return envUrl.replace('http://', 'https://');
      }
      return envUrl;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return 'http://localhost:8000';
  }, []);
  const isProductionApi = useMemo(() => apiBaseUrl.includes('overtaxi.es'), [apiBaseUrl]);
  const isTelegramChannel = useMemo(
    () => Boolean(selectedDeviceId && selectedDeviceId.startsWith('telegram-')),
    [selectedDeviceId]
  );
  const isTelegramReadOnly = useMemo(
    () => process.env.REACT_APP_TELEGRAM_READONLY === 'true' || !isProductionApi,
    [isProductionApi]
  );
  const isTelegramReadonlyActive = isTelegramReadOnly && isTelegramChannel;
  const staticChannels = useMemo(() => ([
    {
      device_id: 'eusta-clientes',
      label: t('admin_chat_channel_eusta'),
      idioma: 'es',
      ultimo_mensaje: t('admin_chat_assistant_eusta'),
      isStatic: true,
    },
    {
      device_id: 'chicho-conductores',
      label: t('admin_chat_channel_chicho'),
      idioma: 'es',
      ultimo_mensaje: t('admin_chat_assistant_chicho'),
      isStatic: true,
    },
  ]), [t]);
  const assistantChannelIds = useMemo(
    () => new Set(staticChannels.map((channel) => channel.device_id)),
    [staticChannels]
  );
  const getFlowType = (channel) => {
    const id = channel?.device_id || '';
    if (id.startsWith('telegram-')) return 'telegram';
    if (id.startsWith('chicho-') || id === 'chicho-conductores') return 'chicho';
    if (id.startsWith('web-')) return 'web';
    return 'web';
  };
  const getAssistantType = (channel) => {
    const id = channel?.device_id || '';
    if (id.startsWith('chicho-') || id === 'chicho-conductores') return 'chicho';
    return 'eusta';
  };
  const combinedChannels = useMemo(() => {
    const map = new Map();
    staticChannels.forEach((channel) => map.set(channel.device_id, channel));
    channels.forEach((channel) => {
      const existing = map.get(channel.device_id);
      if (existing) {
        map.set(channel.device_id, {
          ...channel,
          ...existing,
          ultimo_mensaje: channel.ultimo_mensaje ?? existing.ultimo_mensaje,
          total_mensajes: channel.total_mensajes ?? existing.total_mensajes,
        });
      } else {
        map.set(channel.device_id, channel);
      }
    });
    return Array.from(map.values());
  }, [channels, staticChannels]);
  const filteredChannels = useMemo(() => {
    return combinedChannels.filter((channel) => {
      if (flowFilter !== 'all' && getFlowType(channel) !== flowFilter) return false;
      if (assistantFilter !== 'all' && getAssistantType(channel) !== assistantFilter) return false;
      return true;
    });
  }, [assistantFilter, combinedChannels, flowFilter]);
  const selectedChannel = useMemo(
    () => combinedChannels.find((channel) => channel.device_id === selectedDeviceId),
    [combinedChannels, selectedDeviceId]
  );
  const channelIdsKey = useMemo(
    () => combinedChannels.map((channel) => channel.device_id).sort().join('|'),
    [combinedChannels]
  );
  const chatPattern = `${process.env.PUBLIC_URL}/chat_pattern.png`;
  const assistantLabel = useMemo(
    () => (selectedDeviceId && selectedDeviceId.startsWith('chicho')
      ? t('chat_widget_title_chicho')
      : t('chat_widget_title_eusta')),
    [selectedDeviceId, t]
  );

  const canSend = useMemo(() => input.trim().length > 0, [input]);
  const assistantName = useMemo(
    () => (selectedDeviceId && selectedDeviceId.startsWith('chicho') ? 'Chicho' : 'Eusta'),
    [selectedDeviceId]
  );
  const telegramReasonLabel = useMemo(() => {
    const map = {
      disabled_in_test: t('admin_chat_telegram_reason_disabled'),
      missing_token: t('admin_chat_telegram_reason_missing_token'),
      missing_chat_id: t('admin_chat_telegram_reason_missing_chat_id'),
      connectivity_error: t('admin_chat_telegram_reason_connectivity'),
      http_error: t('admin_chat_telegram_reason_http'),
    };
    return map[telegramReason] || telegramReason;
  }, [t, telegramReason]);

  const logAssistantError = async (detail) => {
    const mensaje = `[AI ERROR] ${detail || 'Error desconocido'}`;
    try {
      await fetch(`${apiBaseUrl}/api/admin/chat-soporte/mensajes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_id: null,
          user_id: null,
          idioma: (selectedChannel?.idioma || 'es').toLowerCase(),
          sender_type: 'admin',
          sender_label: `LOG ${assistantName}`,
          mensaje,
        }),
      });
    } catch {
      // noop
    }
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const fetchAdminIntervals = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/settings/public/chat-soporte`);
        if (!response.ok) return;
        const data = await response.json();

        const parseIntClamped = (value, defaultValue, min, max) => {
          const parsed = Number.parseInt(value, 10);
          if (Number.isNaN(parsed)) return defaultValue;
          return Math.max(min, Math.min(max, parsed));
        };

        setAdminMessagesPollIntervalSeconds(
          parseIntClamped(data?.admin_messages_poll_interval_seconds, 12, 8, 30)
        );
        setAdminChannelsPollIntervalSeconds(
          parseIntClamped(data?.admin_channels_poll_interval_seconds, 30, 15, 60)
        );
        setAdminLogsPollIntervalSeconds(
          parseIntClamped(data?.admin_logs_poll_interval_seconds, 60, 30, 180)
        );
        setAdminTelegramStatusPollIntervalSeconds(
          parseIntClamped(data?.admin_telegram_status_poll_interval_seconds, 60, 30, 180)
        );
      } catch {
        // noop
      }
    };

    fetchAdminIntervals();
  }, [apiBaseUrl, manualRefreshNonce]);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const [eustaRes, chichoRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/admin/chat-soporte/logs/eusta`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${apiBaseUrl}/api/admin/chat-soporte/logs/chicho`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (eustaRes.ok) {
          const data = await eustaRes.json();
          setEustaLogs(data.logs || t('admin_chat_no_logs'));
        }

        if (chichoRes.ok) {
          const data = await chichoRes.json();
          setChichoLogs(data.logs || t('admin_chat_no_logs'));
        }
      } catch {
        // noop
      }
    };

    fetchLogs();

    if (!autoRefreshEnabled || !isPageVisible) return;
    const intervalId = setInterval(fetchLogs, adminLogsPollIntervalSeconds * 1000);
    return () => clearInterval(intervalId);
  }, [adminLogsPollIntervalSeconds, apiBaseUrl, autoRefreshEnabled, isPageVisible, manualRefreshNonce, t, token]);


  useEffect(() => {
    if (!selectedDeviceId && staticChannels.length > 0) {
      setSelectedDeviceId(staticChannels[0].device_id);
    }
  }, [selectedDeviceId, staticChannels]);

  useEffect(() => {
    if (combinedChannels.length === 0) return;
    setAutoRepliesByChannel((prev) => {
      const next = { ...prev };
      combinedChannels.forEach((channel) => {
        if (next[channel.device_id] === undefined) {
          next[channel.device_id] = true;
        }
      });
      return next;
    });
  }, [combinedChannels]);

  useEffect(() => {
    if (!token || combinedChannels.length === 0) return;

    const fetchAutoReplies = async () => {
      try {
        const responses = await Promise.all(
          combinedChannels.map((channel) =>
            fetch(`${apiBaseUrl}/api/admin/chat-soporte/auto-replies/${channel.device_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then((res) => (res.ok ? res.json() : null))
          )
        );

        setAutoRepliesByChannel((prev) => {
          const next = { ...prev };
          responses.forEach((data, index) => {
            if (!data) return;
            const deviceId = combinedChannels[index]?.device_id;
            if (!deviceId) return;
            next[deviceId] = data.enabled !== false;
          });
          return next;
        });
      } catch {
        // noop
      }
    };

    fetchAutoReplies();
  }, [apiBaseUrl, channelIdsKey, combinedChannels, manualRefreshNonce, token]);

  useEffect(() => {
    const fetchTelegramStatus = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/admin/chat-soporte/telegram-status`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setTelegramStatus(Boolean(data.ok));
          setTelegramReason(data.reason || '');
        }
      } catch {
        setTelegramStatus(false);
        setTelegramReason('connectivity_error');
      }
    };

    if (token) {
      fetchTelegramStatus();
      if (!autoRefreshEnabled || !isPageVisible) return;
      const intervalId = setInterval(fetchTelegramStatus, adminTelegramStatusPollIntervalSeconds * 1000);
      return () => clearInterval(intervalId);
    }
  }, [adminTelegramStatusPollIntervalSeconds, apiBaseUrl, autoRefreshEnabled, isPageVisible, manualRefreshNonce, token]);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/admin/chat-soporte/canales`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setChannels(data);
        }
      } catch {
        // noop
      }
    };

    if (token) {
      fetchChannels();
      if (!autoRefreshEnabled || !isPageVisible) return;
      const intervalId = setInterval(fetchChannels, adminChannelsPollIntervalSeconds * 1000);
      return () => clearInterval(intervalId);
    }
  }, [adminChannelsPollIntervalSeconds, apiBaseUrl, autoRefreshEnabled, isPageVisible, manualRefreshNonce, token]);

  useEffect(() => {
    if (!selectedDeviceId) {
      setMessages([]);
      return;
    }

    setMessages([]);
    setLoadingMessages(true);

    const fetchMessages = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/admin/chat-soporte/mensajes/${selectedDeviceId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setMessages((prev) => mergeMessages(data, prev));
        }
      } catch {
        // noop
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
    if (!autoRefreshEnabled || !isPageVisible) return;
    const intervalId = setInterval(fetchMessages, adminMessagesPollIntervalSeconds * 1000);
    return () => clearInterval(intervalId);
  }, [adminMessagesPollIntervalSeconds, apiBaseUrl, autoRefreshEnabled, isPageVisible, manualRefreshNonce, token, selectedDeviceId, mergeMessages]);

  useEffect(() => {
    if (!selectedDeviceId || messages.length === 0) return;
    const latest = messages.reduce((latestDate, msg) => {
      const msgDate = msg?.fecha_envio ? new Date(msg.fecha_envio) : null;
      if (!msgDate) return latestDate;
      if (!latestDate || msgDate > latestDate) return msgDate;
      return latestDate;
    }, null);

    if (!latest) return;
    setLastViewedByChannel((prev) => {
      const current = prev[selectedDeviceId];
      if (!current || latest > current) {
        return { ...prev, [selectedDeviceId]: latest };
      }
      return prev;
    });
  }, [messages, selectedDeviceId]);


  const handleSend = async () => {
    if (!canSend || isSending) return;
    setIsSending(true);
    if (isTelegramReadonlyActive) {
      toast({
        title: t('admin_chat_telegram_readonly_toast'),
        status: 'info',
        duration: 2500,
        isClosable: true,
      });
      setIsSending(false);
      return;
    }
    if (!selectedDeviceId) {
      toast({
        title: t('admin_chat_select_channel'),
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      setIsSending(false);
      return;
    }

    const payload = {
      device_id: selectedDeviceId,
      user_id: null,
      sender_type: 'admin',
      sender_label: assistantChannelIds.has(selectedDeviceId)
        ? t('admin_chat_label_admin')
        : t('admin_chat_label_assistant_virtual'),
      mensaje: input.trim(),
    };

    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/chat-soporte/mensajes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setMessages((prev) => [...prev, data]);
      } else {
        setMessages((prev) => [...prev, {
          ...payload,
          id: `local-${Date.now()}`,
          fecha_envio: new Date().toISOString(),
          local_only: true,
        }]);
      }
      if (assistantChannelIds.has(selectedDeviceId) && autoRepliesByChannel[selectedDeviceId] !== false) {
        try {
          const aiResponse = await fetch(`${apiBaseUrl}/api/chat-soporte/ai-respuesta`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              device_id: selectedDeviceId,
              user_id: null,
              idioma: (selectedChannel?.idioma || 'es').toLowerCase(),
              mensaje: payload.mensaje,
            }),
          });

          if (aiResponse.ok) {
            const data = await aiResponse.json();
            setMessages((prev) => [...prev, data]);
          } else {
            let detail = '';
            try {
              const errorData = await aiResponse.json();
              detail = errorData?.detail || errorData?.message || '';
            } catch {
              try {
                detail = await aiResponse.text();
              } catch {
                detail = '';
              }
            }
            console.error('[AdminChatSoporte] Error AI:', aiResponse.status, detail);
            toast({
              title: t('admin_chat_send_error'),
              description: detail || `HTTP ${aiResponse.status}`,
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            await logAssistantError(detail || `HTTP ${aiResponse.status}`);
          }
        } catch (error) {
          console.error('[AdminChatSoporte] Error AI:', error);
        }
      }
    } catch {
      toast({
        title: t('admin_chat_send_error'),
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
    setInput('');
  };

  const handleClearChannel = async (deviceId) => {
    if (!deviceId || assistantChannelIds.has(deviceId)) return;
    const confirmed = window.confirm(t('admin_chat_delete_confirm'));
    if (!confirmed) return;
    try {
      setAutoRepliesByChannel((prev) => ({
        ...prev,
        [deviceId]: false,
      }));
      fetch(`${apiBaseUrl}/api/admin/chat-soporte/auto-replies/${deviceId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: false }),
      }).catch(() => {});
      const response = await fetch(`${apiBaseUrl}/api/admin/chat-soporte/mensajes/${deviceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        setChannels((prev) => prev.map((channel) => (
          channel.device_id === deviceId
            ? {
                ...channel,
                ultimo_mensaje: null,
                ultimo_sender_type: null,
                ultimo_envio: null,
                total_mensajes: 0,
              }
            : channel
        )));
        if (selectedDeviceId === deviceId) {
          setMessages([]);
        }
        setLastViewedByChannel((prev) => {
          const next = { ...prev };
          delete next[deviceId];
          return next;
        });
      } else {
        toast({
          title: t('admin_chat_delete_error'),
          status: 'error',
          duration: 2500,
          isClosable: true,
        });
      }
    } catch {
      toast({
        title: t('admin_chat_delete_error'),
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      p={6}
      maxW="100%"
      w="100%"
      mx="auto"
      style={{
        backgroundImage: `url(${chatPattern})`,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        backgroundPosition: 'top left',
      }}
    >
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">
          {t('admin_chat_title')} ({t('admin_chat_env_label')} {isProductionApi ? t('admin_chat_env_prod') : t('admin_chat_env_dev')})
        </Heading>
        <VStack align="end" spacing={1}>
          <Text fontSize="sm" color={telegramStatus ? 'green.600' : 'red.500'}>
            {t('admin_chat_telegram_label')}: {telegramStatus ? t('admin_chat_telegram_connected') : t('admin_chat_telegram_disconnected')}
            {!telegramStatus && telegramReasonLabel ? ` (${telegramReasonLabel})` : ''}
          </Text>
          <Text fontSize="xs" color="gray.600">
            Intervalos: mensajes {adminMessagesPollIntervalSeconds}s · canales {adminChannelsPollIntervalSeconds}s · logs {adminLogsPollIntervalSeconds}s · telegram {adminTelegramStatusPollIntervalSeconds}s
          </Text>
          <HStack spacing={3}>
            <Checkbox
              size="sm"
              isChecked={autoRefreshEnabled}
              onChange={(event) => setAutoRefreshEnabled(event.target.checked)}
            >
              Auto-refresco
            </Checkbox>
            <Button
              size="xs"
              onClick={() => setManualRefreshNonce((prev) => prev + 1)}
            >
              Refrescar ahora
            </Button>
          </HStack>
        </VStack>
      </HStack>

      <HStack spacing={4} flexWrap="wrap" mb={2}>
        <Button
          size="sm"
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className="admin-button-primary"
        >
          🔍 {isFiltersOpen ? '▲' : '▼'} {t('admin_chat_filters_title')}
        </Button>
      </HStack>
      <Collapse in={isFiltersOpen} animateOpacity>
        <Box className="filter-section-bg" mb={4}>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text className="filter-section-title">
                {t('admin_chat_filter_flow')}
              </Text>
              <Box className="filter-container">
                <FilterBadge
                  colorScheme="gray"
                  isActive={flowFilter === 'all'}
                  onClick={() => setFlowFilter('all')}
                  label={t('admin_chat_filter_all')}
                />
                <FilterBadge
                  colorScheme="blue"
                  isActive={flowFilter === 'web'}
                  onClick={() => setFlowFilter('web')}
                  label={t('admin_chat_flow_web')}
                />
                <FilterBadge
                  colorScheme="green"
                  isActive={flowFilter === 'telegram'}
                  onClick={() => setFlowFilter('telegram')}
                  label={t('admin_chat_flow_telegram')}
                />
                <FilterBadge
                  colorScheme="orange"
                  isActive={flowFilter === 'chicho'}
                  onClick={() => setFlowFilter('chicho')}
                  label={t('admin_chat_flow_chicho')}
                />
              </Box>
            </Box>
            <Box>
              <Text className="filter-section-title">
                {t('admin_chat_filter_assistant')}
              </Text>
              <Box className="filter-container">
                <FilterBadge
                  colorScheme="gray"
                  isActive={assistantFilter === 'all'}
                  onClick={() => setAssistantFilter('all')}
                  label={t('admin_chat_filter_all')}
                />
                <FilterBadge
                  colorScheme="teal"
                  isActive={assistantFilter === 'eusta'}
                  onClick={() => setAssistantFilter('eusta')}
                  label={t('admin_chat_assistant_eusta_label')}
                />
                <FilterBadge
                  colorScheme="purple"
                  isActive={assistantFilter === 'chicho'}
                  onClick={() => setAssistantFilter('chicho')}
                  label={t('admin_chat_assistant_chicho_label')}
                />
              </Box>
            </Box>
          </VStack>
        </Box>
      </Collapse>

      <HStack align="start" spacing={4}>
        <Box bg="white" borderRadius="md" p={4} border="1px solid" borderColor="gray.200" className="admin-chat-support-channels">
          <Text fontWeight="bold" mb={2}>{t('admin_chat_channels_title')}</Text>
          <VStack spacing={2} align="stretch" maxH="520px" overflowY="auto">
            {filteredChannels.length === 0 ? (
              <Text color="gray.500">{t('admin_chat_no_conversations')}</Text>
            ) : (
              filteredChannels
                .map((channel) => {
                  const lastViewed = lastViewedByChannel[channel.device_id];
                  const ultimoEnvio = channel.ultimo_envio ? new Date(channel.ultimo_envio) : null;
                  const isEmpty = !ultimoEnvio && (!channel.total_mensajes || channel.total_mensajes === 0);
                  const isUnread = Boolean(
                    ultimoEnvio
                    && channel.ultimo_sender_type === 'user'
                    && (!lastViewed || ultimoEnvio > lastViewed)
                    && selectedDeviceId !== channel.device_id
                  );

                  return { channel, isUnread, isEmpty, ultimoEnvio };
                })
                .sort((a, b) => {
                  if (a.isEmpty !== b.isEmpty) return a.isEmpty ? 1 : -1;
                  if (a.isUnread !== b.isUnread) return a.isUnread ? -1 : 1;
                  const timeA = a.ultimoEnvio ? a.ultimoEnvio.getTime() : 0;
                  const timeB = b.ultimoEnvio ? b.ultimoEnvio.getTime() : 0;
                  return timeB - timeA;
                })
                .map(({ channel, isUnread, isEmpty }) => (
                  <Button
                    key={channel.device_id}
                    variant={selectedDeviceId === channel.device_id ? 'solid' : 'outline'}
                    colorScheme="teal"
                    justifyContent="space-between"
                    onClick={() => setSelectedDeviceId(channel.device_id)}
                    className="admin-chat-support-channel-button"
                  >
                    <VStack align="start" spacing={1}>
                      <HStack spacing={2} align="center">
                        {isUnread && <Box className="admin-chat-support-unread-dot" />}
                        <Text fontSize="xs" color={isEmpty ? 'gray.500' : 'inherit'}>
                          {(
                            channel.label
                            || [channel.user_nombre, channel.user_apellidos].filter(Boolean).join(' ')
                            || channel.device_id
                          )} ({(channel.idioma || 'es').toLowerCase()})
                        </Text>
                      </HStack>
                      <HStack spacing={2} align="center">
                        <Checkbox
                          size="sm"
                          isChecked={autoRepliesByChannel[channel.device_id] !== false}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => {
                            event.stopPropagation();
                            const checked = event.target.checked;
                            setAutoRepliesByChannel((prev) => ({
                              ...prev,
                              [channel.device_id]: checked,
                            }));
                            fetch(`${apiBaseUrl}/api/admin/chat-soporte/auto-replies/${channel.device_id}`, {
                              method: 'POST',
                              headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify({ enabled: checked }),
                            }).catch(() => {});
                          }}
                        >
                          {t('admin_chat_auto_replies')}
                        </Checkbox>
                        {!assistantChannelIds.has(channel.device_id) && (
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={t('admin_chat_delete_channel')}
                            icon={<span>🗑️</span>}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClearChannel(channel.device_id);
                            }}
                          />
                        )}
                      </HStack>
                    </VStack>
                  </Button>
                ))
            )}
          </VStack>
        </Box>

        <Box
          flex="1"
          bg="white"
          borderRadius="md"
          p={4}
          border="1px solid"
          borderColor="gray.200"
          className="admin-chat-support-panel"
          style={{
            backgroundImage: `url(${chatPattern})`,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            backgroundPosition: 'top left',
          }}
        >
          {isTelegramReadonlyActive && (
            <Box
              mb={4}
              p={3}
              border="1px solid"
              borderColor="yellow.300"
              bg="yellow.50"
              borderRadius="md"
            >
              <Text fontWeight="bold" fontSize="sm">
                {t('admin_chat_telegram_readonly_title')}
              </Text>
              <Text fontSize="sm">
                {t('admin_chat_telegram_readonly_message')}
              </Text>
            </Box>
          )}

          <HStack mb={4} className="chat-widget-input">
            <Input
              placeholder={t('admin_chat_reply_placeholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              isDisabled={isTelegramReadonlyActive}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button colorScheme="teal" onClick={handleSend} isDisabled={!canSend || isTelegramReadonlyActive || isSending}>
              {t('admin_chat_send')}
            </Button>
          </HStack>

          <VStack spacing={3} align="stretch" maxH="520px" overflowY="auto">
            {messages.length === 0 ? (
              loadingMessages ? (
                <Text color="gray.500">{t('admin_chat_loading_messages')}</Text>
              ) : (
                <Text color="gray.500">{t('admin_chat_no_messages')}</Text>
              )
            ) : (
              filterDuplicates(messages)
                .slice()
                .sort((a, b) => {
                  const timeA = a?.fecha_envio || a?.fechaEnvio ? new Date(a.fecha_envio || a.fechaEnvio).getTime() : 0;
                  const timeB = b?.fecha_envio || b?.fechaEnvio ? new Date(b.fecha_envio || b.fechaEnvio).getTime() : 0;
                  return timeB - timeA;
                })
                .map((msg) => {
                  const senderType = msg.sender_type || msg.senderType;
                  const senderLabel = msg.sender_label || msg.senderLabel;
                  const messageText = msg.mensaje || msg.text || '';
                  const isAssistantChannel = assistantChannelIds.has(selectedDeviceId);
                  const isRemote = isAssistantChannel
                    ? senderType === 'bot'
                    : senderType === 'user';
                  const displayLabel = senderType === 'user'
                    ? (senderLabel || t('admin_chat_label_user'))
                    : senderType === 'admin'
                      ? t('admin_chat_label_admin')
                      : assistantLabel;

                  return (
                    <Box
                      key={msg.id}
                      p={3}
                      borderRadius="md"
                      className={`admin-chat-support-message ${isRemote ? 'admin-chat-support-message-user' : 'admin-chat-support-message-assistant'}`}
                    >
                    <HStack justify="space-between" mb={1}>
                      <Text fontWeight="bold" fontSize="sm">
                        {displayLabel}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(msg.fecha_envio || msg.fechaEnvio).toLocaleString(i18n?.language || 'es-ES')}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" className="admin-chat-support-message-text">{messageText}</Text>
                  </Box>
                  );
                })
            )}
          </VStack>
        </Box>
      </HStack>

      <Box mt={6}>
        <HStack align="start" spacing={4} flexWrap="wrap">
          <Box flex="1" minW="320px" className="admin-logs-container">
            <Box className="admin-logs-header">
              <Text className="admin-logs-title">{t('admin_chat_logs_eusta')}</Text>
            </Box>
            <textarea
              className="admin-logs-textarea"
              readOnly
              value={eustaLogs || t('admin_chat_no_logs')}
            />
          </Box>

          <Box flex="1" minW="320px" className="admin-logs-container">
            <Box className="admin-logs-header">
              <Text className="admin-logs-title">{t('admin_chat_logs_chicho')}</Text>
            </Box>
            <textarea
              className="admin-logs-textarea"
              readOnly
              value={chichoLogs || t('admin_chat_no_logs')}
            />
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

export default AdminChatSoporte;
